import { STAFF_IMPORT_BOUNDS, type StaffImportPreview } from "@lafayette/schemas";
import { type DragEvent, useEffect, useRef, useState } from "react";
import { hintText } from "../../catalog/components/styles";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Notice } from "../../ui/notice";
import { StepCard } from "../components/StepCard";
import { stepActions } from "../components/styles";
import { describeParseErrorPosition, formatBytes, parseErrorMessages } from "../labels";
import { describeUpload, isUploadActive, type UploadState } from "../upload-machine";

type UploadStepProps = Readonly<{
  state: UploadState;
  /** The server has read a file in this session (a new draft). */
  preview: StaffImportPreview | null;
  /** A saved draft is open: its file is fixed. */
  savedDraft: boolean;
  onChoose: (file: File) => void;
  onRetry: () => void;
  onChooseAnother: () => void;
  onContinue: () => void;
}>;

const ACCEPT = ".csv,.tsv,.xlsx,.xls";
const MAX_MEGABYTES = STAFF_IMPORT_BOUNDS.maxBytes / (1024 * 1024);

/**
 * Step 1. A native file input and a drop zone. The browser checks the extension and the size only for
 * fast feedback; the server checks again and reads the file. Errors come from codes and kinds, so no
 * file name or cell text is ever in an error.
 */
export function UploadStep({
  state,
  preview,
  savedDraft,
  onChoose,
  onRetry,
  onChooseAnother,
  onContinue,
}: UploadStepProps) {
  const input = useRef<HTMLInputElement>(null);
  const errorRegion = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const active = isUploadActive(state);
  const failed = state.status === "failed" ? state : null;

  // Focus moves to the error when an upload fails.
  const failureStage = failed === null ? null : `${failed.failedAt}:${failed.message}`;
  useEffect(() => {
    if (failureStage !== null) errorRegion.current?.focus();
  }, [failureStage]);

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file !== undefined && !active) onChoose(file);
  }

  if (savedDraft) {
    return (
      <StepCard step="upload" title="Upload">
        <Notice tone="success">
          This draft was made from a file that is already saved. To use a different file, cancel the
          draft and start a new import.
        </Notice>
      </StepCard>
    );
  }

  return (
    <StepCard step="upload" title="Upload a spreadsheet">
      <p className="m-0">
        Choose a .csv, .tsv, .xlsx or .xls file of up to {MAX_MEGABYTES} MB. The server reads the
        file. Nothing is added to the catalog in this step.
      </p>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop zone is a convenience; the file input inside it is the keyboard path. */}
      <div
        className={cn(
          "grid gap-2 rounded-md border-2 border-dashed border-border bg-background p-4 sm:p-6",
          dragging && "border-primary bg-accent",
        )}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Label htmlFor="import-file-input">Choose a spreadsheet file</Label>
        <p id="import-file-hint" className={hintText}>
          Or drop a file on this area.
        </p>
        <Input
          ref={input}
          id="import-file-input"
          className="h-auto max-w-full py-2.5 compact:h-auto compact:py-1"
          type="file"
          accept={ACCEPT}
          disabled={active}
          aria-describedby={
            failed === null ? "import-file-hint" : "import-file-hint import-file-error"
          }
          aria-invalid={failed === null ? undefined : true}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file !== undefined) onChoose(file);
            // The same file can be chosen again after a failure.
            event.currentTarget.value = "";
          }}
        />
      </div>

      {state.status === "idle" || state.status === "failed" ? null : (
        <p className="m-0 flex items-center gap-2" role="status" aria-live="polite">
          {active ? <Icon name="refresh" className="size-4 animate-spin" /> : null}
          {/* A finished read has its own notice below. */}
          {state.status === "ready" ? null : <span>{describeUpload(state)}</span>}
          <span className="text-sm text-muted-foreground">
            {state.fileName} ({formatBytes(state.byteSize)})
          </span>
        </p>
      )}

      {failed === null ? null : (
        <div
          ref={errorRegion}
          id="import-file-error"
          className="grid gap-2 rounded-sm border border-destructive-border bg-destructive-soft p-4"
          role="alert"
          tabIndex={-1}
        >
          <p className="m-0 flex items-start gap-2 font-semibold text-destructive">
            <Icon name="alert-02" className="mt-1 size-4 shrink-0" />
            <span>{failed.message}</span>
          </p>
          {failed.parseErrors.length > 0 ? (
            <ul className="m-0 list-disc pl-5" aria-label="Problems found in the file">
              {failed.parseErrors.map((error, index) => {
                const position = describeParseErrorPosition(error);
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the list is a fixed answer of the server.
                  <li key={`${error.code}-${index}`}>
                    {position === null ? null : <strong>{position}: </strong>}
                    {parseErrorMessages[error.code]}
                  </li>
                );
              })}
            </ul>
          ) : null}
          <div className={stepActions}>
            {failed.retryable ? (
              <Button type="button" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={onChooseAnother}>
              Choose another file
            </Button>
          </div>
        </div>
      )}

      {state.status === "ready" && preview !== null ? (
        <div className="grid gap-2">
          <Notice tone="success" role="status">
            The file was read: {preview.rowCount} data {preview.rowCount === 1 ? "row" : "rows"} and{" "}
            {preview.columnCount} {preview.columnCount === 1 ? "column" : "columns"}.
          </Notice>
          <div className={stepActions}>
            <Button type="button" onClick={onContinue}>
              Continue to the header row
            </Button>
            <Button type="button" variant="secondary" onClick={onChooseAnother}>
              Choose another file
            </Button>
          </div>
        </div>
      ) : null}
    </StepCard>
  );
}
